import type { CSSProperties, ReactNode } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

export type ConfirmVariant = 'default' | 'destructive';

export interface ConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  zIndex: 50,
};

const contentStyle: CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 32px)',
  maxWidth: 432,
  background: 'var(--dg-surface, white)',
  border: '1px solid var(--dg-border, #e2e8f0)',
  borderRadius: 'var(--dg-r-md, 6px)',
  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15), 0 4px 10px rgba(15, 23, 42, 0.08)',
  padding: 24,
  zIndex: 51,
};

function buttonStyle(opts: { variant: ConfirmVariant | 'cancel'; disabled: boolean }): CSSProperties {
  const base: CSSProperties = {
    height: 36,
    padding: '0 14px',
    borderRadius: 'var(--dg-r-md, 6px)',
    fontSize: 14,
    fontWeight: 500,
    cursor: opts.disabled ? 'not-allowed' : 'pointer',
    opacity: opts.disabled ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: '1px solid',
  };
  if (opts.variant === 'cancel') {
    return {
      ...base,
      background: 'white',
      color: 'var(--dg-text-1, #0f172a)',
      borderColor: 'var(--dg-brand-soft, #e0e7ff)',
    };
  }
  if (opts.variant === 'destructive') {
    return {
      ...base,
      background: 'var(--dg-danger, #dc2626)',
      color: 'white',
      borderColor: 'var(--dg-danger, #dc2626)',
    };
  }
  return {
    ...base,
    background: 'var(--dg-accent-500, #f59e0b)',
    color: 'white',
    borderColor: 'var(--dg-accent-500, #f59e0b)',
  };
}

function Spinner() {
  return (
    <span
      data-testid="dg-confirm-spinner"
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderRightColor: 'transparent',
        animation: 'dg-spin 0.8s linear infinite',
        display: 'inline-block',
      }}
      aria-hidden="true"
    />
  );
}

/**
 * ConfirmModal — declarative AlertDialog wrapper for confirm/cancel flows.
 *
 * Caller controls async close: `onConfirm` is called on click; the dialog
 * does NOT auto-close. To close after a successful action, the caller
 * must `setOpenState(false)` (or use `onOpenChange(false)`) in their own
 * post-mutation flow. This avoids brittle await-ordering when toasts
 * or cleanup follow the mutation.
 */
export function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Onayla',
  cancelText = 'Vazgeç',
  variant = 'default',
  loading = false,
  onConfirm,
}: ConfirmModalProps) {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay style={overlayStyle} data-slot="dg-confirm-overlay" />
        <AlertDialogPrimitive.Content style={contentStyle} data-slot="dg-confirm-content">
          <AlertDialogPrimitive.Title
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: 'var(--dg-brand-deep, #1e1b4b)',
              margin: 0,
            }}
          >
            {title}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description
            style={{
              fontSize: 14,
              color: 'var(--dg-text-2, #334155)',
              marginTop: 8,
              marginBottom: 20,
            }}
          >
            {description}
          </AlertDialogPrimitive.Description>
          <div
            data-slot="dg-confirm-footer"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
          >
            <AlertDialogPrimitive.Cancel asChild>
              <button
                type="button"
                disabled={loading}
                style={buttonStyle({ variant: 'cancel', disabled: loading })}
              >
                {cancelText}
              </button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild>
              <button
                type="button"
                disabled={loading}
                onClick={(e) => {
                  // Prevent Radix auto-close on Action click; caller controls close.
                  e.preventDefault();
                  void onConfirm();
                }}
                style={buttonStyle({ variant, disabled: loading })}
              >
                {loading && <Spinner />}
                {confirmText}
              </button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
